import { Button, Checkbox, Group, Select, Text, TextInput } from '@mantine/core';
import { CaretDown, CaretRight, DownloadSimple, Warning } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import {
    approveDistributionPlan,
    getChannelConfig,
    getChannelProductMappings,
    getPlanShipments,
    getWmsChannelNames,
    type ChannelProductMapping,
    type DistributionPlanDetail,
    type DistributionTransferRow,
} from '@api/distribution';
import { groupTransfersIntoShipments } from '@diagon-alley/common';
import { neutralColors, semanticColors } from '@styles/design-system';
import { showError, showSuccess } from '@utils/notifications';
import { reportWmsPushOutcome } from '@utils/wmsPushOutcome';
import { formatCount } from '@utils/currency';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ShipmentLine {
    transferId: string;
    productId: string;
    productName: string | null;
    quantity: number;
    measurementValue: number | null;
}

interface ShipmentGroup {
    sequenceNumber: number;
    fromId: string;
    fromName: string | null;
    toId: string | null;
    toName: string | null;
    lines: ShipmentLine[];
    totalUnits: number;
    totalMeasurement: number;
}

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function computeShipments(
    transfers: DistributionTransferRow[],
    maxUnitsPerShipment: number | null,
    maxMeasurementPerShipment: number | null,
    channelKey: string,
): ShipmentGroup[] {
    // The packing itself is the shared routine the server runs at approval, so the preview
    // here and the sale orders it then creates split the plan identically. This wrapper only
    // adds the warehouse names the server side does not carry.
    const nameByWarehouse = new Map<string, string | null>();
    for (const t of transfers) {
        nameByWarehouse.set(t.fromWarehouseId, t.fromWarehouseName);
        if (t.toWarehouseId) nameByWarehouse.set(t.toWarehouseId, t.toWarehouseName ?? channelKey);
    }
    const groups = groupTransfersIntoShipments(
        transfers.map(t => ({
            transferId: t.id,
            fromId: t.fromWarehouseId,
            toId: t.toWarehouseId,
            productId: t.productId,
            productName: t.productName,
            quantity: t.approvedQty ?? t.recommendedQty,
            measurementValue: t.measurementValue,
        })),
        { maxUnitsPerShipment, maxMeasurementPerShipment },
    );
    const measurementByTransfer = new Map(transfers.map(t => [t.id, t.measurementValue]));
    return groups.map(g => ({
        sequenceNumber: g.sequenceNumber,
        fromId: g.fromId,
        fromName: nameByWarehouse.get(g.fromId) ?? null,
        toId: g.toId,
        toName: g.toId ? (nameByWarehouse.get(g.toId) ?? channelKey) : channelKey,
        lines: g.lines.map(l => ({ ...l, measurementValue: measurementByTransfer.get(l.transferId) ?? null })),
        totalUnits: g.totalUnits,
        totalMeasurement: g.totalMeasurement,
    }));
}

function formatUnits(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return formatCount(n);
}

function triggerCsvDownload(rows: string[][], fileName: string) {
    const csv = rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

const SHIPMENT_CSV_HEADERS = ['SKU Code', 'Marketplace SKU ID', 'Secondary ID', 'Product Name', 'Quantity'];

function shipmentCsvRows(
    shipment: ShipmentGroup,
    mappingMap: Map<string, { marketplaceSkuId: string; secondaryId: string | null }>,
): string[][] {
    return shipment.lines.map(l => {
        const m = mappingMap.get(l.productId);
        return [l.productId, m?.marketplaceSkuId ?? '', m?.secondaryId ?? '', l.productName ?? '', String(l.quantity)];
    });
}

function downloadShipmentCsv(
    shipment: ShipmentGroup,
    planName: string,
    shipmentName: string,
    mappingMap: Map<string, ChannelProductMapping>,
) {
    triggerCsvDownload(
        [SHIPMENT_CSV_HEADERS, ...shipmentCsvRows(shipment, mappingMap)],
        `${planName} - ${shipmentName}.csv`,
    );
}

/**
 * Every shipment in one file, with Shipment / Destination columns prepended so the rows stay
 * attributable once flattened. Saves downloading each shipment one at a time.
 */
function downloadAllShipmentsCsv(
    shipments: ShipmentGroup[],
    planName: string,
    shipmentNames: string[],
    channelKey: string,
    mappingMap: Map<string, { marketplaceSkuId: string; secondaryId: string | null }>,
) {
    const rows: string[][] = [['Shipment', 'From', 'Destination', ...SHIPMENT_CSV_HEADERS]];
    shipments.forEach((shipment, idx) => {
        const label = shipmentNames[idx] || `Shipment ${shipment.sequenceNumber}`;
        const from = shipment.fromName ?? shipment.fromId ?? '';
        const to = shipment.toName ?? channelKey;
        for (const line of shipmentCsvRows(shipment, mappingMap)) {
            rows.push([label, from, to, ...line]);
        }
    });
    triggerCsvDownload(rows, `${planName} - all shipments.csv`);
}

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
    plan: DistributionPlanDetail;
    opened: boolean;
    onClose: () => void;
    onApproved: () => void;
    selectedTransferIds?: Set<string>;
}

export function ApprovePlanModal({ plan, opened, onClose, onApproved, selectedTransferIds }: Props) {
    const [approving, setApproving] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
    const [wmsType, setWmsType] = useState<string | null>(null);
    const [wmsChannelNames, setWmsChannelNames] = useState<string[]>([]);
    const [selectedChannelName, setSelectedChannelName] = useState<string | null>(null);
    const [createSaleOrders, setCreateSaleOrders] = useState(false);
    const [maxUnitsPerShipment, setMaxUnitsPerShipment] = useState<number | null>(null);
    const [maxMeasurementPerShipment, setMaxMeasurementPerShipment] = useState<number | null>(null);
    const [maxUtilPct, setMaxUtilPct] = useState<number | null>(null);
    const [channelMeasurementUnit, setChannelMeasurementUnit] = useState<'weight' | 'volume' | null>(null);
    const [shipmentNames, setShipmentNames] = useState<string[]>([]);
    const [mappingMap, setMappingMap] = useState<Map<string, ChannelProductMapping>>(new Map());

    useEffect(() => {
        if (!opened) return;
        Promise.all([
            getChannelConfig(plan.channelAbbreviationKey),
            getWmsChannelNames(plan.channelAbbreviationKey),
        ])
            .then(([config, wms]) => {
                setMaxUnitsPerShipment(plan.maxUnitsPerShipment ?? config?.maxUnitsPerShipment ?? null);
                setMaxMeasurementPerShipment(plan.maxMeasurementPerShipment ?? config?.maxMeasurementPerShipment ?? null);
                setMaxUtilPct(plan.maxOutboundUtilizationPct ?? config?.maxOutboundUtilizationPct ?? null);
                setChannelMeasurementUnit(config?.measurementUnit ?? null);
                setWmsType(wms.wmsType);
                setWmsChannelNames(wms.channelNames);
                if (wms.channelNames.length === 1) setSelectedChannelName(wms.channelNames[0]);
            })
            .catch(() => {});

        // Channel-wide rather than per-product: the response is one row per product either way, and
        // passing a few thousand productIds would overflow the request-header limit. Keying by
        // productId is now lossless too — products listed under several marketplace SKUs come back
        // pre-joined with ' | ' instead of one row silently winning.
        getChannelProductMappings(plan.channelAbbreviationKey)
            .then(rows => {
                setMappingMap(new Map(rows.map(r => [r.productId, r])));
            })
            .catch(() => {});
    }, [opened, plan.channelAbbreviationKey]);

    // Only lines that actually move stock: a line saved at 0 (an override or DoH edit that took a
    // source out of the split) is not shipped, so it must not count as a SKU line nor open a
    // shipment on its lane — the server packs the same qty > 0 set, keeping the shipment numbers,
    // names and predicted codes shown here identical to what approval creates.
    const effectiveTransfers = useMemo(() => {
        const moving = plan.transfers.filter(t => !t.isExcluded && (t.approvedQty ?? t.recommendedQty) > 0);
        return selectedTransferIds && selectedTransferIds.size > 0
            ? moving.filter(t => selectedTransferIds.has(t.id))
            : moving;
    }, [plan.transfers, selectedTransferIds]);
    // Non-excluded lines the user deselected → the backend marks these REJECTED (retained, not
    // deleted, and dropped from in-transit). Excluded "cant-send" lines are never part of approval.
    const rejectedCount = useMemo(() =>
        selectedTransferIds && selectedTransferIds.size > 0
            ? plan.transfers.filter(t => !t.isExcluded && !selectedTransferIds.has(t.id)).length
            : 0,
        [plan.transfers, selectedTransferIds],
    );

    const shipments = useMemo(
        () => computeShipments(effectiveTransfers, maxUnitsPerShipment, maxMeasurementPerShipment, plan.channelAbbreviationKey),
        [effectiveTransfers, maxUnitsPerShipment, maxMeasurementPerShipment, plan.channelAbbreviationKey],
    );

    // Lines above what their source can send — the plan view's "avail" (on-hand and inbound
    // in-transit each under the utilisation cap). Approved and pushed exactly as entered; this only
    // makes sure the operator sees them before confirming.
    const availByTransfer = useMemo(() => {
        const util = maxUtilPct ?? 1;
        const m = new Map<string, number>();
        for (const t of effectiveTransfers) {
            const onHand = plan.fromWhStockMap?.[t.fromWarehouseId]?.[t.productId] ?? t.fromWarehouseStock ?? 0;
            const inTransit = plan.fromWhInTransitMap?.[t.fromWarehouseId]?.[t.productId] ?? 0;
            m.set(t.id, Math.floor(onHand * util) + Math.floor(inTransit * util));
        }
        return m;
    }, [effectiveTransfers, plan.fromWhStockMap, plan.fromWhInTransitMap, maxUtilPct]);
    const overAvailTransferIds = useMemo(
        () => new Set(
            effectiveTransfers
                .filter(t => (t.approvedQty ?? t.recommendedQty) > (availByTransfer.get(t.id) ?? 0))
                .map(t => t.id),
        ),
        [effectiveTransfers, availByTransfer],
    );

    // Predicted sale order code per shipment — mirrors the backend buildSaleOrderCode so the user
    // sees the exact code that will be assigned on approve (and pushed to the WMS). PO-driven plans
    // use the source PO number (bare when single shipment); everything else the RPR-{planPart}-{seq}.
    const isPoDrivenCode = plan.demandSource === 'OPEN_PO' && !!plan.sourcePurchaseOrderId;
    const predictSaleOrderCode = (sequenceNumber: number): string => {
        if (isPoDrivenCode) {
            return shipments.length === 1
                ? plan.sourcePurchaseOrderId!
                : `${plan.sourcePurchaseOrderId}-${sequenceNumber}`;
        }
        const planPart = plan.id.replace(/-/g, '').slice(0, 8).toUpperCase();
        return `RPR-${planPart}-${sequenceNumber}`;
    };

    // Sync names array length when shipments change
    useEffect(() => {
        setShipmentNames(prev => {
            const next = shipments.map((_, i) => prev[i] ?? `Shipment ${i + 1}`);
            return next;
        });
    }, [shipments.length]);

    const toggleExpand = (seq: number) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(seq)) next.delete(seq); else next.add(seq);
            return next;
        });
    };

    const handleApprove = async () => {
        setApproving(true);
        const approveArgs = {
            createSaleOrders,
            shipmentNames,
            ...(selectedTransferIds && selectedTransferIds.size > 0 ? { selectedTransferIds: [...selectedTransferIds] } : {}),
            ...(createSaleOrders && selectedChannelName ? { unicommerceChannelName: selectedChannelName } : {}),
        };
        try {
            if (!createSaleOrders) {
                await approveDistributionPlan(plan.id, approveArgs);
                showSuccess('Plan approved');
            } else {
                // Approve + push. The push is synchronous and can time out at the gateway even when the
                // backend completes, so don't trust the promise — derive the verdict from shipment status.
                await approveDistributionPlan(plan.id, approveArgs).catch(() => undefined);
                const shipments = await getPlanShipments(plan.id);
                const attempted = new Set(shipments.map(s => s.saleOrderCode));
                const outcome = reportWmsPushOutcome(shipments, attempted, 'Plan approved');
                if (outcome === 'error') {
                    // Plan is approved but no SO was created — keep the modal open; user retries via Push to WMS.
                    onApproved();
                    return; // finally still resets `approving`; skips onClose so the modal stays open
                }
            }
            onApproved();
            onClose();
        } catch {
            showError('Failed to approve plan. Please try again.');
        } finally {
            setApproving(false);
        }
    };

    if (!opened) return null;

    const totalUnits = effectiveTransfers.reduce((s, t) => s + Math.ceil(t.approvedQty ?? t.recommendedQty), 0);

    return createPortal(
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 24,
            }}
            onClick={e => { if (e.target === e.currentTarget && !approving) onClose(); }}
        >
            <div style={{
                backgroundColor: '#fff', borderRadius: 12, width: '100%', maxWidth: 620,
                maxHeight: '88vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            }}>

                {/* ── Header ── */}
                <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                    <Text style={{ fontSize: 16, fontWeight: 700, color: neutralColors.textPrimary }}>Approve Plan</Text>
                    <Text style={{ fontSize: 13, color: neutralColors.textSecondary, marginTop: 4 }}>
                        {plan.name} · {effectiveTransfers.length} SKU lines · {formatUnits(totalUnits)} units · {shipments.length} shipment{shipments.length !== 1 ? 's' : ''}
                        {maxUnitsPerShipment != null && (
                            <span style={{ color: neutralColors.textMuted }}> · max {formatCount(maxUnitsPerShipment)} units/shipment</span>
                        )}
                        {maxMeasurementPerShipment != null && (
                            <span style={{ color: neutralColors.textMuted }}> · max {formatCount(maxMeasurementPerShipment)} measurement/shipment</span>
                        )}
                    </Text>
                    {overAvailTransferIds.size > 0 && (
                        <div style={{
                            marginTop: 12, padding: '10px 14px', borderRadius: 8,
                            backgroundColor: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
                            display: 'flex', gap: 10, alignItems: 'flex-start',
                        }}>
                            <Warning size={16} color={semanticColors.critical.primary} weight="fill" style={{ flexShrink: 0, marginTop: 1 }} />
                            <Text style={{ fontSize: 13, color: neutralColors.textPrimary }}>
                                <strong>{overAvailTransferIds.size} SKU line{overAvailTransferIds.size !== 1 ? 's' : ''}</strong> exceed the stock available at the source
                                (marked red below). They are approved and pushed to the WMS at the quantities entered — nothing is reduced.
                            </Text>
                        </div>
                    )}
                    {rejectedCount > 0 && (
                        <div style={{
                            marginTop: 12, padding: '10px 14px', borderRadius: 8,
                            backgroundColor: 'rgba(234,88,12,0.06)', border: '1px solid rgba(234,88,12,0.2)',
                            display: 'flex', gap: 10, alignItems: 'flex-start',
                        }}>
                            <Warning size={16} color={semanticColors.warning.primary} weight="fill" style={{ flexShrink: 0, marginTop: 1 }} />
                            <Text style={{ fontSize: 13, color: neutralColors.textPrimary }}>
                                <strong>{rejectedCount} deselected line{rejectedCount !== 1 ? 's' : ''}</strong> will be marked rejected (kept on the plan, excluded from in-transit).
                            </Text>
                        </div>
                    )}
                </div>

                {/* ── Shipment list ── */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
                        <Text style={{ fontSize: 11, fontWeight: 600, color: neutralColors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            Shipments
                            {isPoDrivenCode && (
                                <span style={{ textTransform: 'none', fontWeight: 500, letterSpacing: 0 }}>
                                    {' '}· sale order = PO {plan.sourcePurchaseOrderId}
                                </span>
                            )}
                        </Text>
                        {shipments.length > 0 && (
                            <button
                                type="button"
                                title="Download every shipment's SKU list as one CSV"
                                onClick={() => downloadAllShipmentsCsv(shipments, plan.name, shipmentNames, plan.channelAbbreviationKey, mappingMap)}
                                style={{
                                    flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5,
                                    padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
                                    border: `1px solid ${neutralColors.border}`, background: 'none',
                                    color: neutralColors.textMuted, fontSize: 11, fontWeight: 500,
                                }}
                            >
                                <DownloadSimple size={13} />
                                Download all ({shipments.length})
                            </button>
                        )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {shipments.map((shipment, idx) => {
                            const isExpanded = expandedGroups.has(shipment.sequenceNumber);
                            const name = shipmentNames[idx] ?? `Shipment ${shipment.sequenceNumber}`;
                            return (
                                <div
                                    key={shipment.sequenceNumber}
                                    style={{ border: '1px solid rgba(0,0,0,0.09)', borderRadius: 8, overflow: 'hidden' }}
                                >
                                    {/* Row header */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px' }}>
                                        {/* Sequence badge */}
                                        <div style={{
                                            flexShrink: 0, width: 28, height: 28, borderRadius: 6,
                                            backgroundColor: 'rgba(109,40,217,0.08)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <Text style={{ fontSize: 11, fontWeight: 700, color: '#6d28d9', fontFamily: 'monospace' }}>
                                                {String(shipment.sequenceNumber).padStart(2, '0')}
                                            </Text>
                                        </div>

                                        {/* Editable name */}
                                        <TextInput
                                            value={name}
                                            onChange={e => {
                                                // Read now: the updater runs after React has cleared currentTarget.
                                                const value = e.currentTarget.value;
                                                setShipmentNames(prev => {
                                                    const next = [...prev];
                                                    next[idx] = value;
                                                    return next;
                                                });
                                            }}
                                            size="xs"
                                            style={{ flex: 1, minWidth: 0 }}
                                            styles={{ input: { fontWeight: 500, border: '1px solid transparent', background: 'transparent', padding: '2px 6px' } }}
                                        />

                                        {/* Route */}
                                        <Text style={{ fontSize: 12, color: neutralColors.textMuted, flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>
                                            {shipment.fromName ?? shipment.fromId}
                                            {' → '}
                                            {shipment.toName ?? plan.channelAbbreviationKey}
                                        </Text>

                                        {/* Stats */}
                                        <Text style={{ fontSize: 12, color: neutralColors.textMuted, flexShrink: 0, whiteSpace: 'nowrap' }}>
                                            {shipment.lines.length} SKU{shipment.lines.length !== 1 ? 's' : ''} · {formatUnits(shipment.totalUnits)} units{channelMeasurementUnit && shipment.totalMeasurement > 0 ? ` · ${channelMeasurementUnit === 'weight' ? shipment.totalMeasurement >= 1000 ? `${(shipment.totalMeasurement / 1000).toFixed(2)} kg` : `${shipment.totalMeasurement.toFixed(0)} g` : `${shipment.totalMeasurement.toFixed(4)} CBM`}` : ''}
                                            {(() => {
                                                const over = shipment.lines.filter(l => overAvailTransferIds.has(l.transferId)).length;
                                                return over > 0 ? (
                                                    <span style={{ color: semanticColors.critical.primary, fontWeight: 600 }}> · {over} over avail</span>
                                                ) : null;
                                            })()}
                                        </Text>

                                        {/* CSV download */}
                                        <button
                                            type="button"
                                            title="Download SKU list as CSV"
                                            onClick={() => downloadShipmentCsv(shipment, plan.name, name, mappingMap)}
                                            style={{
                                                flexShrink: 0, padding: 4, borderRadius: 4, border: 'none',
                                                background: 'none', cursor: 'pointer', color: neutralColors.textMuted,
                                                display: 'flex', alignItems: 'center',
                                            }}
                                        >
                                            <DownloadSimple size={14} />
                                        </button>

                                        {/* Expand toggle */}
                                        <button
                                            type="button"
                                            onClick={() => toggleExpand(shipment.sequenceNumber)}
                                            style={{
                                                flexShrink: 0, padding: 4, borderRadius: 4, border: 'none',
                                                background: 'none', cursor: 'pointer', color: neutralColors.textMuted,
                                                display: 'flex', alignItems: 'center',
                                            }}
                                        >
                                            {isExpanded ? <CaretDown size={13} /> : <CaretRight size={13} />}
                                        </button>
                                    </div>

                                    {/* Sale order code footnote — the code assigned on approval and pushed to the WMS */}
                                    <div style={{ padding: '4px 12px 8px 48px' }}>
                                        <Text style={{ fontSize: 11, color: neutralColors.textMuted }}>
                                            Sale order code:{' '}
                                            <span style={{ fontFamily: 'monospace', fontWeight: 600, color: neutralColors.textSecondary }}>
                                                {predictSaleOrderCode(shipment.sequenceNumber)}
                                            </span>
                                        </Text>
                                    </div>

                                    {/* Expanded SKU list */}
                                    {isExpanded && (
                                        <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', backgroundColor: 'rgba(0,0,0,0.015)' }}>
                                            <div style={{ padding: '6px 12px 6px 48px' }}>
                                                {/* Column headers */}
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, marginBottom: 4 }}>
                                                    <Text style={{ fontSize: 10, fontWeight: 600, color: neutralColors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Product</Text>
                                                    <Text style={{ fontSize: 10, fontWeight: 600, color: neutralColors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Qty</Text>
                                                </div>
                                                {shipment.lines.slice(0, 20).map(line => {
                                                    const isOver = overAvailTransferIds.has(line.transferId);
                                                    return (
                                                        <div
                                                            key={line.transferId}
                                                            style={{
                                                                display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, padding: '3px 4px',
                                                                borderBottom: '1px solid rgba(0,0,0,0.04)',
                                                                backgroundColor: isOver ? 'rgba(239,68,68,0.06)' : undefined,
                                                                borderRadius: isOver ? 4 : undefined,
                                                            }}
                                                        >
                                                            <div>
                                                                <Text style={{ fontSize: 12, color: neutralColors.textPrimary, lineHeight: 1.3 }}>{line.productName ?? line.productId}</Text>
                                                                <Text style={{ fontSize: 10, color: neutralColors.textMuted }}>{line.productId}</Text>
                                                            </div>
                                                            <div style={{ textAlign: 'right' }}>
                                                                <Text style={{ fontSize: 12, fontWeight: 600, color: isOver ? semanticColors.critical.primary : neutralColors.textPrimary, whiteSpace: 'nowrap' }}>
                                                                    {formatCount(line.quantity)}
                                                                </Text>
                                                                {isOver && (
                                                                    <Text style={{ fontSize: 10, color: semanticColors.critical.primary, whiteSpace: 'nowrap' }}>
                                                                        avail {formatCount(availByTransfer.get(line.transferId) ?? 0)}
                                                                    </Text>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {shipment.lines.length > 20 && (
                                                    <Text style={{ fontSize: 11, color: neutralColors.textMuted, paddingTop: 6 }}>
                                                        +{shipment.lines.length - 20} more · download CSV to see all
                                                    </Text>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ── Footer ── */}
                <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <Checkbox
                        label={`Push to ${wmsType ? wmsType.charAt(0).toUpperCase() + wmsType.slice(1) : 'WMS'} now`}
                        description="Creates a sale order per shipment in the WMS immediately on approval"
                        size="sm"
                        checked={createSaleOrders}
                        onChange={e => setCreateSaleOrders(e.currentTarget.checked)}
                    />
                    {createSaleOrders && wmsChannelNames.length > 0 && (
                        <Select
                            label={`${wmsType ? wmsType.charAt(0).toUpperCase() + wmsType.slice(1) : 'WMS'} channel`}
                            placeholder="Select channel"
                            size="sm"
                            data={wmsChannelNames.map(n => ({ value: n, label: n }))}
                            value={selectedChannelName}
                            onChange={setSelectedChannelName}
                            searchable
                            comboboxProps={{ withinPortal: true, zIndex: 10000 }}
                        />
                    )}
                    <Group justify="flex-end" gap={8}>
                        <Button variant="subtle" color="dark" size="sm" onClick={onClose} disabled={approving}>
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            color="violet"
                            loading={approving}
                            disabled={createSaleOrders && wmsChannelNames.length > 0 && !selectedChannelName}
                            onClick={handleApprove}
                        >
                            Approve Plan
                        </Button>
                    </Group>
                </div>
            </div>
        </div>,
        document.body,
    );
}
